import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Loader2, AlertTriangle, Lock, Mail, CheckCircle2, KeyRound } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_help-beacon-9/artifacts/ok1e76vb_17%20apr%202026%2C%2009_06_17.png";

export default function BackendLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  useSEO({ title: 'Area Riservata Backend', description: 'Accesso amministratori', noindex: true });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [resetLink, setResetLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/admin');
    } else {
      setError(result.error);
    }
  };

  const handleRecovery = async (e) => {
    e.preventDefault();
    if (!recoveryEmail) return;
    setRecoveryLoading(true);
    setResetLink('');
    try {
      const { data } = await axios.post(`${API}/auth/password-reset/request`, { email: recoveryEmail });
      if (data.link) {
        setResetLink(data.link);
        toast.success('Link di recupero generato!');
      } else {
        toast.info('Se l\'email è registrata, riceverai un link di recupero.');
      }
    } catch {
      toast.error('Errore durante la richiesta');
    } finally {
      setRecoveryLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center px-4" data-testid="backend-login-page">
      <div className="fixed inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '32px 32px'}} />

      <div className="relative z-10 w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#FF3B30]/30" />
          <div className="flex items-center gap-2 px-4 py-1.5 border border-[#FF3B30]/30 bg-[#FF3B30]/5">
            <Lock size={12} className="text-[#FF3B30]" />
            <span className="text-[10px] text-[#FF3B30] font-mono uppercase tracking-[0.2em]">Area Riservata</span>
          </div>
          <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#FF3B30]/30" />
        </div>

        <div className="border border-[#1A2D4D] bg-[#111D30]">
          <div className="border-b border-[#1A2D4D] p-6 text-center">
            <img src={LOGO} alt="LIFE LINKE" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="font-['Barlow_Condensed'] text-2xl font-black uppercase tracking-tight text-white">
              LIFE <span className="text-[#FF3B30]">LINKE</span> - Admin
            </h1>
            <p className="text-xs text-[#4A6B8A] uppercase tracking-widest mt-1">Gestione Servizi LIFE LINKE</p>
          </div>

          <div className="p-6">
            {!showRecovery ? (
              <>
                {error && (
                  <div className="mb-4 p-3 border border-[#FF3B30]/30 bg-[#FF3B30]/10 flex items-center gap-2" data-testid="backend-login-error">
                    <AlertTriangle size={14} className="text-[#FF3B30]" />
                    <span className="text-xs text-[#FF3B30]">{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#4A6B8A] mb-2 font-bold">Email Amministratore</label>
                    <input
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      placeholder="admin@lifelink.it"
                      className="w-full bg-[#0D1B2A] border border-[#1A2D4D] text-white p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm font-mono"
                      data-testid="backend-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#4A6B8A] mb-2 font-bold">Password</label>
                    <input
                      type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                      className="w-full bg-[#0D1B2A] border border-[#1A2D4D] text-white p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm font-mono"
                      data-testid="backend-password-input"
                    />
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all disabled:opacity-50 text-sm"
                    data-testid="backend-login-btn">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
                    Accedi al Backend
                  </button>
                </form>

                <div className="mt-4 text-center">
                  <button
                    onClick={() => setShowRecovery(true)}
                    className="text-xs text-[#4A6B8A] hover:text-[#FF3B30] uppercase tracking-widest transition-colors"
                    data-testid="backend-forgot-link"
                  >
                    <KeyRound size={12} className="inline mr-1" />
                    Password dimenticata?
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white text-center mb-2">
                  <KeyRound size={16} className="inline mr-2 text-[#FF3B30]" />
                  Recupera Password
                </h2>
                <p className="text-[10px] text-[#4A6B8A] text-center mb-4 uppercase tracking-widest">
                  Inserisci la tua email per ricevere il link di reset
                </p>

                <form onSubmit={handleRecovery} className="space-y-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#4A6B8A] mb-2 font-bold">Email</label>
                    <input
                      type="email" value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} required
                      className="w-full bg-[#0D1B2A] border border-[#1A2D4D] text-white p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm font-mono"
                      data-testid="backend-recovery-email"
                    />
                  </div>
                  <button type="submit" disabled={recoveryLoading}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#FF3B30] text-white font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all disabled:opacity-50 text-sm"
                    data-testid="backend-send-reset-btn">
                    {recoveryLoading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                    Invia Link
                  </button>
                </form>

                {resetLink && (
                  <div className="mt-4 p-3 border border-[#32D74B]/30 bg-[#32D74B]/5" data-testid="backend-reset-link-box">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 size={12} className="text-[#32D74B]" />
                      <span className="text-[10px] text-[#32D74B] uppercase tracking-widest font-bold">Link generato</span>
                    </div>
                    <a href={resetLink} className="block text-xs text-[#0A84FF] hover:text-[#FF3B30] break-all underline font-mono" data-testid="backend-reset-link">
                      {resetLink}
                    </a>
                    <p className="text-[8px] text-[#2A3F5F] mt-2 uppercase tracking-widest">Scade tra 30 minuti</p>
                  </div>
                )}

                <div className="mt-4 text-center">
                  <button onClick={() => { setShowRecovery(false); setResetLink(''); }}
                    className="text-xs text-[#4A6B8A] hover:text-[#FF3B30] uppercase tracking-widest transition-colors"
                    data-testid="backend-back-login">
                    Torna al Login
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="border-t border-[#1A2D4D] px-6 py-3 flex items-center justify-between">
            <span className="text-[9px] text-[#2A3F5F] uppercase tracking-widest font-mono">Accesso riservato</span>
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-[#32D74B] rounded-full animate-pulse" />
              <span className="text-[9px] text-[#32D74B] font-mono uppercase tracking-widest">Sistema Attivo</span>
            </div>
          </div>
        </div>
        <p className="text-center text-[9px] text-[#2A3F5F] uppercase tracking-widest mt-6">&copy; {new Date().getFullYear()} ninfa.eu</p>
      </div>
    </div>
  );
}
